import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';

// Lead mínimo necessário para abrir a janela de anotações de atendimento.
export interface ServiceNotesLead {
  id: number;
  name: string;
}

interface ServiceNotesContextData {
  openLeads: ServiceNotesLead[];
  openServiceNotes: (lead: ServiceNotesLead) => void;
  closeServiceNotes: (leadId: number) => void;
}

const ServiceNotesContext = createContext<ServiceNotesContextData | undefined>(undefined);

// Várias janelas podem ficar abertas ao mesmo tempo (uma por lead) e continuam
// abertas enquanto o usuário navega pelo sistema.
export function ServiceNotesProvider({ children }: { children: ReactNode }) {
  const [openLeads, setOpenLeads] = useState<ServiceNotesLead[]>([]);

  const openServiceNotes = useCallback((lead: ServiceNotesLead) => {
    setOpenLeads(prev => (prev.some(l => l.id === lead.id) ? prev : [...prev, { id: lead.id, name: lead.name }]));
  }, []);

  const closeServiceNotes = useCallback((leadId: number) => {
    setOpenLeads(prev => prev.filter(l => l.id !== leadId));
  }, []);

  const value = useMemo(() => ({ openLeads, openServiceNotes, closeServiceNotes }), [openLeads, openServiceNotes, closeServiceNotes]);

  return <ServiceNotesContext.Provider value={value}>{children}</ServiceNotesContext.Provider>;
}

export function useServiceNotes() {
  const context = useContext(ServiceNotesContext);
  if (!context) {
    throw new Error('useServiceNotes must be used within a ServiceNotesProvider');
  }
  return context;
}
